import { defineStore } from 'pinia'
import { watch } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'system'

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyThemeToDocument(dark: boolean): void {
  document.documentElement.classList.toggle('dark', dark)
  document.documentElement.classList.add('theme-ready')
}

/**
 * Options API store so pinia-plugin-persistedstate hydration merges plain state.
 * Setup stores with `ref` state break on $patch: the ref is replaced by a string and toggles stop working.
 */
export const useThemeStore = defineStore('theme', {
  state: () => ({
    theme: 'system' as ThemePreference,
  }),
  getters: {
    isDark(state): boolean {
      if (state.theme === 'dark') return true
      if (state.theme === 'light') return false
      return systemPrefersDark()
    },
  },
  actions: {
    applyTheme(): void {
      applyThemeToDocument(this.isDark)
    },
    setTheme(next: ThemePreference): void {
      this.theme = next
      applyThemeToDocument(this.isDark)
    },
    toggleTheme(): void {
      this.setTheme(this.isDark ? 'light' : 'dark')
    },
    /**
     * Watches `theme` state so any direct $patch (e.g. from pinia-plugin-persistedstate
     * hydration or devtools) also triggers a DOM update without going through setTheme.
     */
    startWatcher(): void {
      watch(
        () => this.theme,
        () => applyThemeToDocument(this.isDark),
        { immediate: false },
      )
    },
  },
  persist: {
    key: 'tb_admin_theme',
    storage: localStorage,
    paths: ['theme'],
  },
})
